import * as legacyNav from '@panwds/react-nav'
import * as sparkyNav from '@panwds/sparky-react-nav'
import { renderTitle, getNavBadgeProps, getSubNavListUrlWithDefaultOpen } from './navUtils'
import FavoriteComponent from './FavoriteComponent'
import { __info } from '../../init/debug'
import { getFrameworkVars } from '../../framework/states'
import './SubNavItem.scss'
import type { VFC } from 'react'
import type { NavItem } from '../../framework/microAppTypes'
import type { FavoritePreferences } from '../../framework/stateTypes'

type OnGroupFn = (groupId: number | undefined) => void
export type SubNavItemProps = {
  item: NavItem
  activeSubNavItem?: NavItem | undefined
  hasSubNavSibling?: boolean
  groupId?: number
  openedItem?: number
  hoveredItem?: number
  onHover?: OnGroupFn
  onOpen?: OnGroupFn
  favorites?: FavoritePreferences
}
export const SubNavItem: VFC<SubNavItemProps> = ({
  item,
  activeSubNavItem,
  hasSubNavSibling,
  groupId,
  openedItem,
  hoveredItem,
  onHover,
  onOpen,
  favorites
}) => {

  const useScmNavStyleUpdate = getFrameworkVars()?.use_scm_nav_style_update
  const { AccordionWrapper, SubNavList, SubNavListItem } = useScmNavStyleUpdate ? sparkyNav : legacyNav

  if (!item?.path && (!item.children || item.children.length === 0)) {
    __info('returning empty nav')
    return <></>
  }
  if (!item.title) {
    __info('ignore nav item without title', item)
    return <></>
  }

  const groupProps = {
    groupId,
    openedItem,
    hoveredItem,
    onHover,
    onOpen,
  }
  const title = renderTitle(item.title) || ''
  if (!item.children) {
    const isActive = activeSubNavItem === item ||
      Boolean(item.path && item.path !== '#' && activeSubNavItem?.path === item.path) // for settings
    const recordId = `nav:${(item.path || item.key)!.replace('#', '')}`
    const Item = hasSubNavSibling ? SubNavList : SubNavListItem
    const fav = !item._early_loading && !item.disabled && <FavoriteComponent navItem={item} favorites={favorites || []} active={isActive} useScmNavStyleUpdate={useScmNavStyleUpdate}/>
    const containerClasses = useScmNavStyleUpdate ? 'spf-flex sparky-sub-nav-item-container-updated' : 'spf-flex sparky-sub-nav-item-container'
    const itemClasses = useScmNavStyleUpdate ? 'spf-flex-1 spaky-nav-subNavItem-updated' : 'spf-flex-1 spaky-nav-subNavItem'
    return (
      <div className={containerClasses}>
        <div className={itemClasses}>
          <Item
            title={title as string}
            id={item.key}
            key={item.key}
            url={item.navPath || item.path!}
            disabled={item.disabled as boolean}
            active={isActive}
            dataTestId={recordId}
            dataMetrics={item.dataMetrics || recordId}
            addClassName={itemClasses}
            {...hasSubNavSibling && groupProps}
            {...getNavBadgeProps(item)}
          />
        </div>
        {fav}
      </div>
    )
  }
  else {
    const key = item.key || item.path || String(item.title)
    const hasSubNavSibling = item.children.some((child) => child?.children?.length)
    const renderedChildren = item.children.map((child, i) => <SubNavItem
      key={child.key || child.path || `ch-${i}`}
      item={child}
      activeSubNavItem={activeSubNavItem}
      hasSubNavSibling={hasSubNavSibling}
      favorites={favorites}
    />)
    return (
      <SubNavList
        title={title as string}
        id={key}
        key={key}
        url={getSubNavListUrlWithDefaultOpen(item, activeSubNavItem)}
        defaultOpen={item.defaultOpen}
        {...groupProps}
      >
        {!item.multiAccordion && hasSubNavSibling ? (
          <AccordionWrapper>{renderedChildren}</AccordionWrapper>
        ) : renderedChildren}
      </SubNavList>
    )
  }
}

export default SubNavItem
